import { useState, useEffect } from 'react';
import './Coupen.css';
import Layout from '../../../components/Layout';
import AdminNavbar from '../../../components/Admin/AdminNavbar/AdminNavbar';
import CreateCoupen from '../../../components/Admin/Coupen/CreateCoupen/CreateCoupen';
import DeleteCoupen from '../../../components/Admin/Coupen/DeleteCoupen/DeleteCoupen';
import EditCoupen from '../../../components/Admin/Coupen/EditCoupen/EditCoupen';
import { Box, IconButton, Tooltip, Avatar, Typography } from '@mui/material';

import DeleteIcon from '../../../assets/Admin/Banner/delete.svg';
import EditIcon from '../../../assets/Admin/Banner/edit.svg';
import { axiosInstance } from '../../../utils/tokenUtils';
import { formatDateTime as formatDateTimeUtil } from '../../../utils/dateUtils';
import { toast } from 'react-hot-toast';
import Lottie from 'lottie-react';
import bannersAnimation from '../../../assets/Admin/banners.json';
import CouponSkeleton from '../../../components/SkeletonEffect/CouponSkeleton/CouponSkeleton';

const CoupenFallback = ({ code, name }) => {
  const getInitials = text => {
    if (!text) return 'CP';
    return text
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className="coupen-fallback">
      <Avatar
        className="coupen-avatar"
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: '#0a5ef3',
          fontSize: '48px',
          fontWeight: 'bold',
        }}
      >
        {getInitials(name || code)}
      </Avatar>
      <div className="coupen-fallback-text">{name || code || 'Coupon'}</div>
    </div>
  );
};

const Coupen = () => {
  const [activeCoupens, setActiveCoupens] = useState([]);
  const [deletedCoupens, setDeletedCoupens] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('active');
  const [activePage, setActivePage] = useState(1);
  const [deletedPage, setDeletedPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(15);
  const [activeCount, setActiveCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    coupenId: null,
    coupenName: '',
  });

  const [editModal, setEditModal] = useState({
    isOpen: false,
    coupenId: null,
  });

  // Helper function to convert local date to UTC for API calls
  const convertLocalToUTC = dateString => {
    if (!dateString) return '';

    // Parse the date string and create a local date
    const localDate = new Date(dateString);

    // Convert to UTC by adding timezone offset
    const utcDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);

    // Format as YYYY-MM-DD
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Helper function to convert UTC date to local for display
  const convertUTCToLocal = dateString => {
    if (!dateString) return '';

    // Parse the UTC date string
    const utcDate = new Date(dateString);

    // Convert to local timezone
    const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

    // Format as YYYY-MM-DD
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const fetchActiveCoupens = async (page = activePage) => {
    try {
      setActiveLoading(true);
      const response = await axiosInstance.get('/admin/coupons', {
        params: { page, limit: pageLimit },
      });
      const coupensWithLocalDates = response.data.results.map(coupen => ({
        ...coupen,
        valid_from: convertUTCToLocal(coupen.valid_from),
        valid_until: convertUTCToLocal(coupen.valid_until),
      }));
      setActiveCoupens(coupensWithLocalDates);
      setActiveCount(response.data.count || coupensWithLocalDates.length || 0);
    } catch (error) {
      console.error('Error fetching active coupons:', error);
    } finally {
      setActiveLoading(false);
    }
  };

  const fetchDeletedCoupens = async (page = deletedPage) => {
    try {
      setDeletedLoading(true);
      const response = await axiosInstance.get('/admin/coupons', {
        params: { page, limit: pageLimit, status: 'deleted' },
      });
      const coupensWithLocalDates = response.data.results.map(coupen => ({
        ...coupen,
        valid_from: convertUTCToLocal(coupen.valid_from),
        valid_until: convertUTCToLocal(coupen.valid_until),
      }));
      setDeletedCoupens(coupensWithLocalDates);
      setDeletedCount(response.data.count || coupensWithLocalDates.length || 0);
    } catch (error) {
      console.error('Error fetching deleted coupons:', error);
    } finally {
      setDeletedLoading(false);
    }
  };

  // Initial fetch for active coupons and when activePage or pageLimit changes
  useEffect(() => {
    fetchActiveCoupens(activePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageLimit]);

  // Fetch deleted coupons when tab is selected or page/deps change while on deleted tab
  useEffect(() => {
    if (selectedTab === 'deleted') {
      fetchDeletedCoupens(deletedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, deletedPage, pageLimit]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    const id = deleteModal.coupenId;
    if (id) {
      try {
        // Get the coupon data to send with status update
        const coupen = [...activeCoupens, ...deletedCoupens].find(coupen => coupen.id === id);
        if (coupen) {
          // Convert dates to UTC before sending to API
          const couponData = {
            code: coupen.code,
            name: coupen.name,
            description: coupen.description,
            coupon_type: coupen.coupon_type,
            discount_percentage: parseFloat(coupen.discount_percentage),
            valid_from: convertLocalToUTC(coupen.valid_from),
            valid_until: convertLocalToUTC(coupen.valid_until),
            status: 'deleted',
          };

          await axiosInstance.delete(`/admin/coupon/${id}`, { data: couponData });
          // Refresh lists to reflect deletion
          await Promise.all([fetchActiveCoupens(activePage), fetchDeletedCoupens(deletedPage)]);

          toast.error('Coupon deleted successfully');
        }
      } catch (error) {
        console.error('Error deactivating coupon:', error);
      } finally {
        // Close the delete modal
        setDeleteModal({ isOpen: false, coupenId: null, coupenName: '' });
      }
    }
  };

  const handleDeleteClick = id => {
    // Get the coupen name
    const coupen = [...activeCoupens, ...deletedCoupens].find(coupen => coupen.id === id);
    // Open the delete confirmation modal
    setDeleteModal({
      isOpen: true,
      coupenId: id,
      coupenName: coupen?.name || 'this coupon',
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ isOpen: false, coupenId: null, coupenName: '' });
  };

  const handleCoupenCreated = async newCoupen => {
    const coupenWithLocalDates = {
      ...newCoupen,
      valid_from: convertUTCToLocal(newCoupen.valid_from),
      valid_until: convertUTCToLocal(newCoupen.valid_until),
    };
    setActiveCoupens([...activeCoupens, coupenWithLocalDates]);
    setActiveCount(activeCount + 1);
    handleCloseModal();
  };

  const handleEditCoupen = coupenId => {
    setEditModal({
      isOpen: true,
      coupenId: coupenId,
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({
      isOpen: false,
      coupenId: null,
    });
  };

  const handleCoupenUpdated = updatedCoupen => {
    setActiveCoupens(
      activeCoupens.map(coupen =>
        coupen.id === updatedCoupen.id
          ? {
              ...updatedCoupen,
              valid_from: convertUTCToLocal(updatedCoupen.valid_from),
              valid_until: convertUTCToLocal(updatedCoupen.valid_until),
            }
          : coupen
      )
    );
    setDeletedCoupens(
      deletedCoupens.map(coupen =>
        coupen.id === updatedCoupen.id
          ? {
              ...updatedCoupen,
              valid_from: convertUTCToLocal(updatedCoupen.valid_from),
              valid_until: convertUTCToLocal(updatedCoupen.valid_until),
            }
          : coupen
      )
    );
    handleCloseEditModal();
  };

  const handleCreateCoupen = () => {
    setIsModalOpen(true);
  };

  const handleDeleteCoupen = coupenId => {
    // TODO: Implement delete coupen functionality
    console.log('Delete coupen:', coupenId);
  };

  const formatDate = dateString => {
    if (!dateString) return '';

    // Parse the date string (already in local timezone from convertUTCToLocal)
    const date = new Date(dateString);

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = dateString => {
    if (!dateString) return '';
    return formatDateTimeUtil(dateString, 'DD MMM YYYY, h:mm A');
  };

  // Helper function to format coupon type for display
  const formatCouponType = couponType => {
    const typeMap = {
      teacher_enrollment: 'Instructor Enrollment',
      student_wallet_topup: 'Learner Wallet Top-up',
    };
    return typeMap[couponType] || couponType;
  };

  return (
    <>
      <Layout title="Klass Online | Coupons">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <div className="coupen-container">
              <div className="coupen-header">
                <Box className="coupen-title">Coupons</Box>
                <button className="create-coupen-btn" onClick={handleCreateCoupen}>
                  <span>+</span> Create Coupon
                </button>
              </div>
              <div className="coupen-tabs">
                <button
                  className={`coupen-tab ${selectedTab === 'active' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('active')}
                >
                  Active
                </button>
                <button
                  className={`coupen-tab ${selectedTab === 'deleted' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('deleted')}
                >
                  Deleted
                </button>
              </div>
              <div className="coupen-content">
                {(selectedTab === 'active' ? activeLoading : deletedLoading) ? (
                  <CouponSkeleton />
                ) : (selectedTab === 'active' ? activeCoupens : deletedCoupens).length > 0 ? (
                  <div className="coupens-list">
                    {(selectedTab === 'active' ? activeCoupens : deletedCoupens).map(coupen => (
                      <div key={coupen.id} className="coupen-item">
                        <div className="coupen-header-section">
                          <div className="coupen-main-info">
                            <div className="coupen-code-badge">
                              <span className="code-label">CODE</span>
                              <span className="code-value">{coupen.code}</span>
                            </div>
                            <div className="coupen-title-section">
                              <h3 className="coupen-name">{coupen.name}</h3>
                              <p className="coupen-description">{coupen.description}</p>
                            </div>
                          </div>
                          <div className="coupen-actions">
                            {selectedTab === 'active' && (
                              <>
                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() => handleDeleteClick(coupen.id)}
                                    size="small"
                                    className="delete-btn"
                                  >
                                    <img src={DeleteIcon} alt="Delete" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() => handleEditCoupen(coupen.id)}
                                    size="small"
                                    className="edit-btn"
                                  >
                                    <img src={EditIcon} alt="Edit" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {selectedTab === 'deleted' && (
                              <div className="deleted-header-info">
                                <span className="status-badge deleted">Deleted</span>
                                <span className="deleted-date">
                                  {formatDateTime(coupen.updated_at)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="coupen-details-grid">
                          <div className="detail-row">
                            <div className="detail-column">
                              <div className="detail-item">
                                <label className="detail-label">Type</label>
                                <span className="detail-value">
                                  {formatCouponType(coupen.coupon_type)}
                                </span>
                              </div>
                            </div>
                            <div className="detail-column">
                              <div className="detail-item">
                                <label className="detail-label">Discount</label>
                                <span className="detail-value discount-value">
                                  {coupen.discount_percentage}%
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="detail-row">
                            <div className="detail-column">
                              <div className="detail-item">
                                <label className="detail-label">Valid From</label>
                                <span className="detail-value">
                                  {formatDate(coupen.valid_from)}
                                </span>
                              </div>
                            </div>
                            <div className="detail-column">
                              <div className="detail-item">
                                <label className="detail-label">Valid Until</label>
                                <span className="detail-value">
                                  {formatDate(coupen.valid_until)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {false && selectedTab === 'deleted' && <div />}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-container">
                    {selectedTab === 'active' ? (
                      activeCount === 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 300,
                              height: 300,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Lottie
                              animationData={bannersAnimation}
                              loop={true}
                              autoplay={true}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontSize: '16px',
                              fontWeight: 500,
                              color: '#94a3b8',
                              textAlign: 'center',
                            }}
                          >
                            Start Creating Coupon
                          </Typography>
                        </Box>
                      ) : (
                        <p className="coupen-empty-state">No coupons on this page</p>
                      )
                    ) : deletedCount === 0 ? (
                      <p className="coupen-empty-state">No deleted coupons</p>
                    ) : (
                      <p className="coupen-empty-state">No deleted coupons on this page</p>
                    )}
                  </div>
                )}
              </div>

              <Box className="coupen-pagination-container">
                <Box className="coupen-pagination">
                  <Box className="coupen-pagination-info">
                    {(() => {
                      const isActive = selectedTab === 'active';
                      const count = isActive ? activeCount : deletedCount;
                      return (
                        <>
                          <Typography className="coupen-pagination-text">Showing</Typography>
                          <select
                            className="coupen-pagination-select"
                            aria-label="Rows per page"
                            value={pageLimit}
                            onChange={e => {
                              const newLimit = Number(e.target.value);
                              setPageLimit(newLimit);
                              setActivePage(1);
                              setDeletedPage(1);
                            }}
                          >
                            {[10, 15, 20, 30].map(size => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                          <Typography className="coupen-pagination-text">
                            of {count} coupons
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                  <Box className="coupen-pagination-controls">
                    <button
                      className="coupen-pagination-button"
                      onClick={() =>
                        selectedTab === 'active'
                          ? setActivePage(Math.max(1, activePage - 1))
                          : setDeletedPage(Math.max(1, deletedPage - 1))
                      }
                      disabled={(selectedTab === 'active' ? activePage : deletedPage) === 1}
                    >
                      Previous
                    </button>
                    <span className="coupen-pagination-number">
                      {(selectedTab === 'active' ? activePage : deletedPage) || 1}
                    </span>
                    <button
                      className="coupen-pagination-button"
                      onClick={() => {
                        const isActive = selectedTab === 'active';
                        const page = isActive ? activePage : deletedPage;
                        const count = isActive ? activeCount : deletedCount;
                        const totalPages = Math.max(1, Math.ceil(count / pageLimit));
                        if (isActive) {
                          setActivePage(Math.min(totalPages, page + 1));
                        } else {
                          setDeletedPage(Math.min(totalPages, page + 1));
                        }
                      }}
                      disabled={(() => {
                        const isActive = selectedTab === 'active';
                        const page = isActive ? activePage : deletedPage;
                        const count = isActive ? activeCount : deletedCount;
                        const totalPages = Math.max(1, Math.ceil(count / pageLimit));
                        return page >= totalPages;
                      })()}
                    >
                      Next
                    </button>
                  </Box>
                </Box>
              </Box>

              {isModalOpen && (
                <CreateCoupen onClose={handleCloseModal} onSuccess={handleCoupenCreated} />
              )}

              <DeleteCoupen
                isOpen={deleteModal.isOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleDeleteConfirm}
                coupenName={deleteModal.coupenName}
              />

              <EditCoupen
                isOpen={editModal.isOpen}
                onClose={handleCloseEditModal}
                onSuccess={handleCoupenUpdated}
                couponId={editModal.coupenId}
              />
            </div>
          </Box>
        </Box>
      </Layout>
    </>
  );
};

export default Coupen;
